<template>
  <div class="auto-complete">
    <div
      v-click-outside="close"
      class="inner"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      aria-owns="list-component"
      @keydown.down="handleArrowDown"
      @keydown.up="handleArrowUp"
      @keydown.escape="close"
    >
      <TextInput
        class="text-input"
        :value="displayValue"
        :errors="errors"
        :theme="theme"
        :name="name"
        :placeholder="placeholder"
        :pattern="pattern"
        :title-label="label"
        :tooltip-content="tooltipContent"
        :tooltip-label="tooltipLabel"
        :required="required"
        show-clear-nbtn
        @input="handleInput"
        @focus="isOpen = true"
        @blur="$emit('blur', $event)"
      />
      <List
        v-if="showList"
        ref="list"
        v-model="inputValue"
        autocomplete
        :class="['list', { listOnTop }]"
        :options="options"
        :max-results="maxResults"
        @input="handleSelect"
        @reset="$emit('input', '')"
      />
    </div>
  </div>
</template>

<script>
import clickOutsideDirective from '@/directives/click-outside'

import List from './List'

export default {
  name: 'AutoComplete',

  components: {
    List,
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
  },

  directives: {
    'click-outside': clickOutsideDirective,
  },

  props: {
    errors: {
      type: Object,
      default: undefined,
    },

    options: {
      default: () => [],
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },

    maxResults: {
      type: Number,
      default: undefined,
    },

    minLength: {
      type: Number,
      default: undefined,
    },
    name: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    pattern: {
      type: String,
      default: '',
    },

    theme: {
      default: 'white',
      validator: theme => ['white', 'green', 'gray'].includes(theme),
    },

    value: {
      type: [String, Number],
      default: '',
    },

    tooltipContent: {
      type: String,
      default: '',
    },

    tooltipLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isOpen: false,
      listOnTop: false,
      inputValue: this.value,
    }
  },

  computed: {
    displayValue() {
      const val = this.options.find(({ value }) => value === this.inputValue)
      return val ? val.name : this.inputValue
    },

    hasTooShortInput() {
      return this.minLength && this.inputValue.length < this.minLength
    },

    showList() {
      return !this.hasTooShortInput && this.isOpen && this.options.length
    },
  },

  watch: {
    value(value) {
      this.inputValue = value
    },
    isOpen: {
      handler(value) {
        this.$emit('isSuggestionOpen', value)
      },
      immediate: true,
    },
  },

  methods: {
    close() {
      this.isOpen = false
      this.listOnTop = false
    },

    checkVisibility() {
      const box = this.$refs.list.getBoundingClientRect()
      const isInViewport = box.top >= 0 && box.bottom <= window.innerHeight
      this.listOnTop = !isInViewport
    },

    handleArrowDown(e) {
      if (this.hasTooShortInput) {
        return
      }
      if (!this.isOpen) {
        this.open()
        return
      }

      e.preventDefault()
      this.$refs.list.focusNext()
    },

    handleArrowUp(e) {
      if (this.hasTooShortInput) {
        return
      }
      if (!this.isOpen) {
        this.open()
        return
      }

      e.preventDefault()
      this.$refs.list.focusPrevious()
    },

    handleInput(value) {
      this.$emit('input', value)
    },

    handleSelect(value) {
      this.$emit('input', value)
      this.$nextTick(this.close)
    },

    open() {
      this.isOpen = true
      this.$nextTick(this.checkVisibility)
    },

    toggle() {
      if (this.hasTooShortInput) {
        return
      }

      this.isOpen
        ? this.close()
        : this.$emit('input', this.inputValue) && this.open()
    },
  },
}
</script>

<style lang="postcss" scoped>
.auto-complete {
  width: 100%;

  .text-input {
    width: 100%;
  }
}

.inner {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

.list {
  position: absolute;
  top: 53px;
  left: 0;
  right: 0;

  &.listOnTop {
    bottom: calc(100% + 1px);
    top: auto;
  }
}
</style>
